import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, Inject, Input, OnDestroy, OnInit, signal, TemplateRef, WritableSignal } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { MatBadgeModule } from '@angular/material/badge';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatDrawerMode, MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { StorageService } from '@damofire/data-angular-commons';
import { AuthenticationService, User } from '@damofire/data-security';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { captureMessage } from '@sentry/angular';
import { Subject, Subscription, timer } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { AutoUnsubscribe } from '../auto-unsubscribe.decorator';
import { CheckForUpdateService } from '../check-for-update/check-for-update.service';
import { CookiePolicyComponent } from '../cookie-policy/cookie-policy.component';
import { DisabledOfflineDirective } from '../directive/disabled-offline.directive';
import { EmitterEvent, EmitterService } from '../emitter.service';
import { FORM_DEBOUNCE_TIME } from '../global';
import { ACCEPT_COOKIES_ENABLED, APP_TARGET, FAQ_URL_EN, FAQ_URL_FR, SERVICE_DESK_URL, WEBSITE_URL } from '../injection-tokens';
import { hasAnyRole, SURVEY_ROLES } from '../security/user-role-utils';
import { LangSelectorComponent } from '../lang-selector/lang-selector.component';
import { MainFooterComponent } from '../main-footer/main-footer.component';
import { SystemNotificationCenterComponent } from '../system-notification-center/system-notification-center.component';
import { UserNotification } from '../system-notification-center/system-notification.dto';
import { SystemNotificationService } from '../system-notification-center/system-notification.service';
import { MainLayoutAuthService } from './main-layout-auth.service';
import { LoaderWrapperComponent } from '../loader/loader-wrapper.component';

const PREFS_STORAGE_KEY = 'tt.uix.mainMenuState';
const EVENT_CHANNEL_NAME = 'MainLayoutComponent';

export const TOGGLE_PREVIEW_MENU = 'togglePreviewMenu';
export const HIDE_PREVIEW_MENU = 'hidePreviewMenu';
export const SHOW_PREVIEW_MENU = 'showPreviewMenu';
export const PREVIEW_MENU_ALWAYS_OPEN = 'previewMenuAlwaysOpen';
export const TOGGLE_MENU = 'toggleMenu';
export const HIDE_MENU = 'hideMenu';
export const SHOW_MENU = 'showMenu';
export const SELECTION_CHANGED = 'selectionChanged';
export const CLEAR_SELECTION = 'clearSelection';
export const MOBILE_UPDATE_NOTIFICATION = 'mobileUpdateAvailable';

@AutoUnsubscribe()
@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: ['./main-layout.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatSidenavModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuModule,
    MatBadgeModule,
    MatToolbarModule,
    MatListModule,
    MatDividerModule,
    DisabledOfflineDirective,
    LangSelectorComponent,
    LoaderWrapperComponent,
    MainFooterComponent,
    SystemNotificationCenterComponent,
    TranslatePipe,
  ],
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  userDetails: WritableSignal<User | null> = signal(null);

  shouldMenuBeOpened = true;
  shouldPreviewBeClosed = true;
  FULL_SIDE_MENU_WIDTH = 350;
  notificationsMenuMode: MatDrawerMode = 'push';

  @Input()
  showLoading = false;

  @Input()
  loadingMessage: string;

  @Input()
  requireLogin = true;

  @Input()
  previewItems: unknown[] = [];

  @Input()
  footerTemplateRef: TemplateRef<HTMLElement>;

  @Input()
  mainTemplateRef: TemplateRef<HTMLElement>;

  @Input()
  mainPreviewTemplateRef: TemplateRef<HTMLElement>;

  @Input()
  mainPreviewAlternateRoute: string;

  previewMenuAlwaysOpen = false;

  @Input()
  hideFooter = false;

  private readonly eventEmitter: Subject<EmitterEvent>;
  private readonly eventSubs = new SubSink();
  private notificationSub: Subscription;
  notifications: UserNotification[];

  updateAvailable = false;
  notificationCount = 0;
  displayNotifications = false;
  hasOfflineNavigationAccess = false;
  public static channelName = (): string => EVENT_CHANNEL_NAME;

  constructor(
    private readonly mainLayoutAuthService: MainLayoutAuthService,
    private readonly authService: AuthenticationService,
    private readonly storage: StorageService,
    private readonly emitterService: EmitterService,
    private readonly bottomSheet: MatBottomSheet,
    private readonly title: Title,
    private readonly translateService: TranslateService,
    private readonly notificationService: SystemNotificationService,
    private readonly breakpointObserver: BreakpointObserver,
    @Inject(ACCEPT_COOKIES_ENABLED) private readonly acceptCookiesEnabled: boolean,
    @Inject(SERVICE_DESK_URL) public serviceDeskUrl: string,
    @Inject(FAQ_URL_EN) private faqUrlEn: string,
    @Inject(FAQ_URL_FR) private faqUrlFr: string,
    @Inject(WEBSITE_URL) public websiteUrl: string,
    @Inject(APP_TARGET) public appTarget: string,
    private readonly checkForUpdateService: CheckForUpdateService,
  ) {
    this.eventEmitter = emitterService.get(MainLayoutComponent.channelName());
  }

  ngOnInit(): void {
    if (this.appTarget === 'hosting') {
      this.shouldMenuBeOpened = this.storage.getItem(PREFS_STORAGE_KEY, 'true') === 'true';
    } else {
      this.shouldMenuBeOpened = false;
    }

    // If there are preselected items to preview
    this.shouldPreviewBeClosed = this.previewItems.length <= 0;

    const mainTitle = this.translateService.instant('main.title');
    this.title.setTitle(mainTitle);

    this.listen();
  }

  faqUrl(): string {
    return this.translateService.currentLang === 'fr' ? this.faqUrlFr : this.faqUrlEn;
  }

  ngOnDestroy(): void {
    this.previewItems = null;
    this.footerTemplateRef = null;
    this.mainTemplateRef = null;
    this.mainPreviewTemplateRef = null;
  }

  toggleSideMenu(): void {
    this.shouldMenuBeOpened = !this.shouldMenuBeOpened;
    this.saveMenuState();
  }

  shouldShowPreviewPane(): boolean {
    return this.previewItems.length > 0 && this.mainPreviewTemplateRef && !this.shouldPreviewBeClosed;
  }

  togglePreviewMenu(): void {
    this.shouldPreviewBeClosed = !this.shouldPreviewBeClosed;
  }

  logout(): void {
    this.authService.logout();
  }

  update(): void {
    this.checkForUpdateService.update();
  }

  updateMenuState(): void {
    const windowWidth = document.documentElement.clientWidth;
    let remainingWidth = windowWidth - this.FULL_SIDE_MENU_WIDTH;

    if (this.shouldMenuBeOpened) {
      remainingWidth -= this.FULL_SIDE_MENU_WIDTH;
    }

    if (!this.shouldPreviewBeClosed && remainingWidth <= 600) {
      this.shouldMenuBeOpened = false;
      this.saveMenuState();
    }
  }

  toggleNotificationCenter(forceClose = false): void {
    if (forceClose) {
      this.displayNotifications = false;
    } else {
      this.displayNotifications = !this.displayNotifications;
    }
  }

  getNotificationCount(): number | null {
    return this.notificationCount + (this.updateAvailable ? 1 : 0) || null;
  }

  private listen(): void {
    this.eventSubs.add(
      this.eventEmitter.subscribe((event: EmitterEvent) => this.handleEvents(event)),
      this.checkForUpdateService.versionUpdated().subscribe(() => (this.updateAvailable = true)),
      this.translateService.onLangChange.subscribe(() => {
        const mainTitle = this.translateService.instant('main.title');
        this.title.setTitle(mainTitle);
      }),
      this.authService.subscribe({
        next: (user: User) => {
          console.log(`User infos`, user);
          this.userDetails.set(user);
          this.refreshOfflineNavigationAccess();

          if (user?._id && user.firstName && user.lastName) {
            this.notificationSub?.unsubscribe();
            this.notificationSub = this.notificationService
              .getNotifications(user._id)
              .pipe(
                debounceTime(FORM_DEBOUNCE_TIME),
                map((notifications) => notifications.filter((n) => !n.hidden)),
              )
              .subscribe(this.handleNotifications);
          } else {
            const { _id, firstName, lastName, organization } = user || {};
            const msg = `initUserAuthentication empty vars: _id: ${_id} | firstName : ${firstName} | lastName: ${lastName} | orgName: ${organization?.name} | orgID: ${organization?._id}`;
            captureMessage(msg);
          }
        },
      }),
      this.breakpointObserver.observe([Breakpoints.Small, Breakpoints.XSmall]).subscribe((state) => {
        this.notificationsMenuMode = 'push';

        if (state.matches) {
          this.shouldMenuBeOpened = false;
          this.notificationsMenuMode = 'over';
        }
      }),
    );

    if (this.appTarget === 'app') {
      const hourlyCheck = 1000 * 60 * 60;
      this.eventSubs.add(timer(5000, hourlyCheck).subscribe({ next: () => this.notificationService.checkForAppUpdates() }));
    }

    if (this.acceptCookiesEnabled && !localStorage.getItem('cookiesAccepted')) {
      const bottomSheetRef = this.bottomSheet.open(CookiePolicyComponent);

      const sub = bottomSheetRef.afterDismissed().subscribe(() => {
        localStorage.setItem('cookiesAccepted', 'true');
        sub.unsubscribe();
      });
    }
  }

  private handleEvents(event: EmitterEvent): void {
    switch (event.event) {
      case SELECTION_CHANGED:
        this.previewItems = Array.isArray(event.value) ? event.value : [event.value];
        this.toggleNotificationCenter(true);
        this.shouldPreviewBeClosed = false;
        break;
      case CLEAR_SELECTION:
        this.previewItems = [];
        this.shouldPreviewBeClosed = true;
        break;
      case SHOW_MENU:
        this.shouldMenuBeOpened = true;
        this.storage.setItem(PREFS_STORAGE_KEY, this.shouldMenuBeOpened ? 'true' : 'false');
        break;
      case HIDE_MENU:
        this.shouldMenuBeOpened = false;
        this.storage.setItem(PREFS_STORAGE_KEY, this.shouldMenuBeOpened ? 'true' : 'false');
        break;
      case TOGGLE_MENU:
        this.toggleSideMenu();
        break;
      case SHOW_PREVIEW_MENU:
        this.shouldPreviewBeClosed = false;
        this.toggleNotificationCenter(true);
        break;
      case PREVIEW_MENU_ALWAYS_OPEN:
        this.previewMenuAlwaysOpen = event.value;
        break;
      case HIDE_PREVIEW_MENU:
        this.shouldPreviewBeClosed = true;
        break;
      case MOBILE_UPDATE_NOTIFICATION:
        this.addMobileUpdateNotification();
        break;
      case TOGGLE_PREVIEW_MENU:
      default:
        this.togglePreviewMenu();
    }
  }

  private readonly handleNotifications = (notifications: UserNotification[]): void => {
    this.notifications = notifications.filter((n) => !n.remove);
    this.notificationCount = this.notifications.filter((n) => !n.read).length;
  };

  private saveMenuState(): void {
    this.storage.setItem(PREFS_STORAGE_KEY, this.shouldMenuBeOpened ? 'true' : 'false');
  }

  private refreshOfflineNavigationAccess(): void {
    this.authService
      .roles()
      .then((roles) => (this.hasOfflineNavigationAccess = hasAnyRole(roles, SURVEY_ROLES)))
      .catch(() => (this.hasOfflineNavigationAccess = false));
  }

  private addMobileUpdateNotification(): void {
    const userNotification: UserNotification = {
      read: false,
      user: this.userDetails(),
      messageKey: 'avian.notification.mobile.update',
      type: 'notification',
      severity: 'default',
      mobileOnly: true,
      audit: { createdDate: Timestamp.now() },
    };
    const match = this.notifications.find((n) => n.mobileOnly === true);
    if (!match) {
      this.notifications.push(userNotification);
    }
    this.handleNotifications(this.notifications);
  }
}
